import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Menu, Search, Home, Trophy, Rocket, User } from 'lucide-react';
import { Logo } from '@/components/ui/Logo';
import { LanguageSwitcher } from '@/components/ui/LanguageSwitcher';
import { useUIStore } from '@/stores/uiStore';
import { fetchProfile, getLauncherUrl } from '@/lib/launcherApi';

export function Header() {
  const { t } = useTranslation();
  const { toggleMobileMenu } = useUIStore();
  const [user, setUser] = useState<{ name: string } | null>(null);

  // Fetch user on mount
  useEffect(() => {
    fetchProfile().then(setUser);
  }, []);

  const goToLauncher = () => {
    // Cookie-based auth - no token needed in URL
    window.location.href = `${getLauncherUrl()}/command-center`;
  };

  const goToAchievements = () => {
    // Cookie-based auth - no token needed in URL
    window.location.href = `${getLauncherUrl()}/achievements`;
  };

  return (
    <header className="sticky top-0 z-50 bg-space-900/80 backdrop-blur-md border-b border-cosmic-purple/20">
      <div className="flex items-center justify-between h-16 px-4 lg:px-6">
        {/* Mobile menu button */}
        <button
          onClick={toggleMobileMenu}
          className="lg:hidden p-2 hover:bg-space-700 rounded-lg transition-colors"
          aria-label="Toggle menu"
        >
          <Menu className="w-6 h-6" />
        </button>

        {/* Logo */}
        <div className="hidden lg:block">
          <Logo />
        </div>

        {/* Mobile logo (centered) */}
        <div className="lg:hidden">
          <Link to="/" className="text-2xl">🦁</Link>
        </div>

        {/* Navigation */}
        <nav className="hidden lg:flex items-center gap-6">
          <button
            onClick={goToLauncher}
            className="flex items-center gap-2 px-3 py-2 text-blue-400 hover:text-blue-300 hover:bg-blue-900/30 rounded-lg transition-colors"
            title={t('common.center')}
          >
            <Rocket className="w-4 h-4" />
            <span>{t('common.center')}</span>
          </button>
          <NavLink to="/" icon={<Home className="w-4 h-4" />}>
            {t('common.home')}
          </NavLink>
          <NavLink to="/search" icon={<Search className="w-4 h-4" />}>
            {t('common.search')}
          </NavLink>
          <button
            onClick={goToAchievements}
            className="flex items-center gap-2 px-3 py-2 text-yellow-400 hover:text-yellow-300 hover:bg-yellow-900/30 rounded-lg transition-colors"
            title={t('common.achievements')}
          >
            <Trophy className="w-4 h-4" />
            <span>{t('common.achievements')}</span>
          </button>
        </nav>

        {/* User info and language switcher */}
        <div className="flex items-center gap-2">
          <LanguageSwitcher />
          <div className="hidden md:flex items-center gap-2 px-3 py-1.5 bg-space-700/50 rounded-full">
            <User className="w-4 h-4 text-gray-400" />
            <span className="text-sm text-gray-300">{user?.name || t('common.guest')}</span>
          </div>
        </div>
      </div>
    </header>
  );
}

interface NavLinkProps {
  to: string;
  icon: React.ReactNode;
  children: React.ReactNode;
}

function NavLink({ to, icon, children }: NavLinkProps) {
  return (
    <Link
      to={to}
      className="flex items-center gap-2 px-3 py-2 text-gray-300 hover:text-white hover:bg-space-700/50 rounded-lg transition-colors"
    >
      {icon}
      <span>{children}</span>
    </Link>
  );
}
